import React from 'react';
import styled from '@emotion/styled';
import type { CommonHeaderProps, Nullable, UspBar as UspBarProps } from 'corgi-types';
import type { DisplayProps } from 'styled-system';
import { backgroundColor, display } from 'styled-system';

import type { SearchOptions } from 'common/components/Search/hooks/types';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import type { BackgroundColorProps } from 'common/theme/system-types';
import type { PrimaryNavigationItemProps } from 'common/types/NavigationItem';
import type { SearchParams } from 'common/types/search';
import Toolbar from './toolbar/Toolbar';
import TopNavigation from './top-navigation/TopNavigation';
import UspBar from './usp-bar/UspBar';

interface GlobalHeaderProps extends CommonHeaderProps {
  navigation: Nullable<{
    primary: PrimaryNavigationItemProps[];
  }>;
  tabLabels?: string[];
  uspBar: Nullable<UspBarProps>;
  searchParams?: SearchParams;
  searchOptions?: SearchOptions;
  isMinimized?: boolean;
  isZipCode?: boolean;
  hideUSPBarOnMobile?: boolean;
}

const Container = styled.header<BackgroundColorProps>`
  ${backgroundColor};
`;

const UspBarContainer = styled.div<DisplayProps>`
  ${display};
`;

const useGlobalHeaderLogic = ({
  uspBar,
  navigation,
  isMinimized,
  hideUSPBarOnMobile,
}: Pick<GlobalHeaderProps, 'uspBar' | 'navigation' | 'isMinimized' | 'hideUSPBarOnMobile'>) => {
  const shouldHideUspBar = hideUSPBarOnMobile ? false : isMinimized;
  const withUspBar = uspBar && !shouldHideUspBar;
  const withHideNavOnDesktop = useFeatureFlagIsOn(GrowthbookFlag.HideNavOnDesktop);

  const withTopNavigation = navigation && !isMinimized && !withHideNavOnDesktop;

  return { withUspBar, withTopNavigation };
};

const GlobalHeader = ({
  uspBar,
  navigation,
  tabLabels = [],
  isMinimized = false,
  hideUSPBarOnMobile = false,
  isZipCode = false,
  searchParams = {},
  searchOptions = {},
  ...restProps
}: GlobalHeaderProps) => {
  const { withUspBar, withTopNavigation } = useGlobalHeaderLogic({
    uspBar,
    navigation,
    isMinimized,
    hideUSPBarOnMobile,
  });

  return (
    <Container data-corgi-id="global-header" bg="sys.neutral.background.default">
      {withUspBar && (
        <UspBarContainer display={hideUSPBarOnMobile ? ['none', null, 'flex'] : undefined}>
          <UspBar {...uspBar} />
        </UspBarContainer>
      )}
      <Toolbar
        searchParams={searchParams}
        searchOptions={searchOptions}
        {...restProps}
        isMinimized={isMinimized}
        isZipCode={isZipCode}
        tabLabels={tabLabels}
      />
      {withTopNavigation && <TopNavigation navigation={navigation?.primary || []} />}
    </Container>
  );
};

export default GlobalHeader;
